import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';

export interface LegalSection {
  id: string;
  label: string;
}

@Component({
  selector: 'app-legal-page-layout',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './legal-page-layout.html',
})
export class LegalPageLayoutComponent implements OnInit, OnDestroy {
  @Input() kicker = 'Legal';
  @Input() title = '';
  @Input() description = '';
  @Input() metaLines: string[] = [];
  @Input() sections: LegalSection[] = [];

  // Signals so scroll-driven updates (outside any Angular-tracked event) still
  // schedule change detection in this app's zoneless setup (no zone.js polyfill).
  readonly activeId = signal('');
  readonly showBackToTop = signal(false);

  private platformId = inject(PLATFORM_ID);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private scrollHandler = () => this.onScroll();

  ngOnInit(): void {
    this.activeId.set(this.sections[0]?.id ?? '');
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.removeEventListener('scroll', this.scrollHandler);
  }

  private onScroll(): void {
    this.showBackToTop.set(window.scrollY > 600);

    const offset = 112; // matches the `.legal-content section` scroll-mt-28 (7rem) in styles.css
    let current = this.sections[0]?.id ?? '';

    for (const section of this.sections) {
      const el = this.elementRef.nativeElement.querySelector<HTMLElement>(`#${CSS.escape(section.id)}`);
      if (!el) continue;
      if (el.getBoundingClientRect().top - offset <= 0) {
        current = section.id;
      }
    }

    this.activeId.set(current);
  }

  scrollToTop(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // The app has <base href="/">, so a plain fragment-only href ("#id") resolves
  // against that base and drops the current path, sending the router to "/".
  // Scroll manually instead and update the hash without letting the browser
  // navigate.
  jumpToSection(event: Event, id: string): void {
    event.preventDefault();
    if (!isPlatformBrowser(this.platformId)) return;

    history.replaceState(null, '', `${location.pathname}${location.search}#${id}`);

    // No need to set activeId here — the scroll listener below will pick up
    // the right section as the page settles into its new position.
    const el = this.elementRef.nativeElement.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (!el) return;

    const offset = 112; // matches the `.legal-content section` scroll-mt-28 (7rem) in styles.css
    const targetY = window.scrollY + el.getBoundingClientRect().top - offset;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }
}
